import {
  SentimentDissatisfiedRounded,
  SentimentNeutralRounded,
  SentimentVeryDissatisfiedRounded,
  SentimentVerySatisfiedRounded,
} from '@mui/icons-material'
import { Box, SxProps, Typography } from '@mui/material'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import KumocanButton from 'components/button'
import KumocanTextareaAutosize from 'components/textArea'
import { assignApi } from 'hooks/api/assign'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import SatisfactionSlider from 'pages/assign/components/satisfactionSlider'
import { SECURITY_REVIEW } from 'types/assign'
import {
  ASSIGN_REVIEW_CATEGORY,
  ASSIGN_REVIEW_VERSION,
} from 'types/assignReview'
import { PRIMARY_COLOR } from 'valiable'

interface Props {
  assignId: string
  onClose: () => void
}

interface Form {
  satisfaction: number // 旧アンケート
  assignReviewNote: string

  assignReviews: {
    category: number // enum使う
    version: number // enum使う
    value: number
  }[]
}

// 既存のアンケート結果との兼ね合いで、100だけずらしてます
// 既存はenum値で5とか25とかに値が入ってます
const REVIEW_OFFSET = 100

export default function SurveyV2(props: Props): JSX.Element {
  const { t } = useTranslation(['page-assign-components', 'common'])
  const { showSnackbar } = useSnackbar()
  const { control, handleSubmit } = useForm<Form>({
    defaultValues: {
      satisfaction: 51, // 初期値は51
      assignReviewNote: '',
      assignReviews: [
        {
          category: ASSIGN_REVIEW_CATEGORY.Equipment,
          version: ASSIGN_REVIEW_VERSION.V2,
          value: 50,
        },
        {
          category: ASSIGN_REVIEW_CATEGORY.Planning,
          version: ASSIGN_REVIEW_VERSION.V2,
          value: 50,
        },
        {
          category: ASSIGN_REVIEW_CATEGORY.Environment,
          version: ASSIGN_REVIEW_VERSION.V2,
          value: 50,
        },
        {
          category: ASSIGN_REVIEW_CATEGORY.Communication,
          version: ASSIGN_REVIEW_VERSION.V2,
          value: 50,
        },
        {
          category: ASSIGN_REVIEW_CATEGORY.Condition,
          version: ASSIGN_REVIEW_VERSION.V2,
          value: 50,
        },
      ],
    },
  })
  const [step, setStep] = useState<1 | 2 | 3>(1)

  const onSubmit = async (form: Form) => {
    setStep(3)
    try {
      await assignApi().updateSecurityReview(
        props.assignId,
        form.satisfaction + REVIEW_OFFSET,
        form.assignReviewNote,
      )
      await assignApi().updateAssignReview(props.assignId, form.assignReviews)
      setStep(3)
    } catch {
      showSnackbar(t('survey_common.submit_error'), 'error')
    }
  }

  const handleClose = () => {
    props.onClose()
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
          padding: '60px 0 80px',
        }}
      >
        <Typography
          sx={{
            width: '100%',
            fontSize: '23px',
            fontWeight: 'bold',
            color: PRIMARY_COLOR,
            paddingBottom: '6px',
            borderBottom: '1px solid',
          }}
        >
          {t('survey_common.title_v2')}
        </Typography>
        <Typography sx={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>
          {step === 2
            ? t('survey_common.privacy_notice_step2')
            : t('survey_common.privacy_notice_step1')}
        </Typography>
        <Box sx={separatorStyle} />

        {step === 1 ? (
          // Step 1: 勤務満足度調査
          <>
            <Box sx={legContainerStyle}>
              <Typography sx={legTitleStyle}>
                {t('survey_v2.legend_title')}
              </Typography>
              <Box sx={legWrapperStyle}>
                <Box sx={legGradientBarStyle} />
                <Box sx={legItemStyle}>
                  <SentimentVeryDissatisfiedRounded
                    sx={{ color: '#FF005A', fontSize: '42px' }}
                  />
                  <Typography sx={legLabelStyle}>
                    {t('survey_v2.legend_considered_quitting')}
                  </Typography>
                </Box>

                <Box sx={legItemStyle}>
                  <SentimentDissatisfiedRounded
                    sx={{ color: '#CF46A8', fontSize: '42px' }}
                  />
                  <Typography sx={legLabelStyle}>
                    {t('survey_v2.legend_uncertain')}
                  </Typography>
                </Box>

                <Box sx={legItemStyle}>
                  <SentimentNeutralRounded
                    sx={{ color: '#6B7ACE', fontSize: '42px' }}
                  />
                  <Typography sx={legLabelStyle}>
                    {t('survey_v2.legend_satisfied')}
                  </Typography>
                </Box>

                <Box sx={legItemStyle}>
                  <SentimentVerySatisfiedRounded
                    sx={{ color: '#14AAD3', fontSize: '42px' }}
                  />
                  <Typography sx={legLabelStyle}>
                    {t('survey_v2.legend_willing_continue')}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={headerSeparatorStyle} />

            <Box sx={surveyListStyle}>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="satisfaction"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.question_overall')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Box sx={separatorStyle} />
              </Box>
              <Typography sx={titleStyle}>
                {t('survey_v2.question_categories')}
              </Typography>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="assignReviews.0.value"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.category_equipment')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Box sx={separatorStyle} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="assignReviews.1.value"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.category_planning')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Box sx={separatorStyle} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="assignReviews.2.value"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.category_environment')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Box sx={separatorStyle} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="assignReviews.3.value"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.category_communication')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Box sx={separatorStyle} />
              </Box>
              <Box sx={{ width: '100%' }}>
                <Controller
                  name="assignReviews.4.value"
                  control={control}
                  render={({ field }) => (
                    <SatisfactionSlider
                      title={t('survey_v2.category_condition')}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Box>
              <Box sx={separatorStyle} />
            </Box>

            <Typography sx={surveyLabelStyle}>
              {t('survey_v2.label_feedback')}
            </Typography>

            <Controller
              name="assignReviewNote"
              control={control}
              render={({ field }) => (
                <KumocanTextareaAutosize
                  id="note"
                  onInput={field.onChange}
                  placeholder={t('survey_v2.placeholder_feedback')}
                  style={{
                    width: '100%',
                    minHeight: '100px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    padding: '8px',
                    fontSize: '16px',
                  }}
                />
              )}
            />

            <KumocanButton
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              style={{
                backgroundColor: '#14b7ae',
                color: '#ffffff',
                minWidth: '100px',
                height: '60px',
                fontSize: '20px',
                marginTop: '10px',
                width: '100%',
                fontWeight: 'bold',
              }}
            >
              {t('common:buttons.submit')}
            </KumocanButton>
          </>
        ) : (
          // Step 3: Thanksモーダル
          <>
            <Typography
              sx={{ marginTop: '8px', fontSize: '20px', fontWeight: 'bold' }}
            >
              {t('survey_common.thank_you')}
            </Typography>
            <img
              src="/images/thanks.png"
              alt="Thanks"
              style={{
                maxWidth: '70%',
                height: 'auto',
              }}
            />
            <KumocanButton
              onClick={handleClose}
              variant="contained"
              color="inherit"
              style={{
                height: '60px',
                fontSize: '20px',
                marginTop: '10px',
                width: '100%',
              }}
            >
              {t('common:buttons.close')}
            </KumocanButton>
          </>
        )}
      </Box>
    </>
  )
}

export const makeSecurityReviewOptions = (t: (key: string) => string) => [
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SentimentVeryDissatisfiedRounded
          sx={{ color: '#FF005A', fontSize: '40px' }}
        />
        <Typography
          sx={{
            textAlign: 'left',
            fontSize: '20px',
            fontWeight: 'bold',
            whiteSpace: 'pre-wrap',
          }}
        >
          {t('survey_v1.option_considered_quitting')}
        </Typography>
      </Box>
    ),
    value: SECURITY_REVIEW.CONSIDERED_QUITTING,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SentimentDissatisfiedRounded
          sx={{ color: '#CF46A8', fontSize: '40px' }}
        />
        <Typography
          sx={{ textAlign: 'left', fontSize: '20px', fontWeight: 'bold' }}
        >
          {t('survey_v1.option_unpleasant')}
        </Typography>
      </Box>
    ),
    value: SECURITY_REVIEW.UNPLEASANT,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SentimentNeutralRounded sx={{ color: '#6B7ACE', fontSize: '40px' }} />

        <Typography
          sx={{
            textAlign: 'left',
            fontSize: '20px',
            fontWeight: 'bold',
            whiteSpace: 'pre-wrap',
          }}
        >
          {t('survey_v1.option_had_better')}
        </Typography>
      </Box>
    ),
    value: SECURITY_REVIEW.HAD_BETTER_BEFORE,
  },
  {
    label: (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SentimentVerySatisfiedRounded
          sx={{ color: '#14AAD3', fontSize: '40px' }}
        />

        <Typography
          sx={{ textAlign: 'left', fontSize: '20px', fontWeight: 'bold' }}
        >
          {t('survey_v1.option_willing_continue')}
        </Typography>
      </Box>
    ),
    value: SECURITY_REVIEW.WILLING_TO_CONTINUE,
  },
]

const separatorStyle: SxProps = {
  width: '100%',
  margin: '0 auto',
  borderBottom: '1px solid #ccc',
}

const headerSeparatorStyle: SxProps = {
  ...separatorStyle,
  borderBottom: `1px solid ${PRIMARY_COLOR}`,
  marginTop: '16px',
}

const surveyLabelStyle: SxProps = {
  marginTop: '8px',
  fontSize: '22px',
  fontWeight: 'bold',
  textAlign: 'left',
}

const surveyListStyle: SxProps = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}

const titleStyle: SxProps = {
  marginTop: '16px',
  fontSize: '16px',
  fontWeight: 'bold',
}

const legContainerStyle: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}

const legGradientBarStyle: SxProps = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '6px',
  background: 'linear-gradient(to bottom, #f50057, #00e5ff)',
  borderRadius: '3px',
}

const legWrapperStyle: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxWidth: 300,
  width: '80%',
  position: 'relative',
  paddingLeft: '20px',
}

const legTitleStyle: SxProps = {
  fontWeight: 'bold',
  fontSize: '18px',
  marginBottom: '8px',
}

const legItemStyle: SxProps = {
  display: 'flex',
  alignItems: 'center',
  fontWeight: 'bold',
}

const legLabelStyle: SxProps = {
  fontWeight: 'bold',
  fontSize: '18px',
  marginLeft: '16px',
  wordBreak: 'break-word',
}
